import { styled } from '@linaria/react';
import { Group, Stack } from '@mantine/core';
import { inkLight } from '../../styles/design-tokens';
import { Divider } from '../../ui-library/divider/divider';
import { Icon } from '../../ui-library/icon/icon';
import { Radio } from '../../ui-library/radio/radio';
import { Text } from '../../ui-library/typography/typography';
import type { FilterGroupRadio } from './filter-panel.type';

type Props<T extends FilterGroupRadio> = {
  group: T;
  toggleCollapsed: (group: T) => void;
  isCollapsed: boolean;
  hasDivider?: boolean;
};

export const FilterPanelGroupRadio = <T extends FilterGroupRadio>({
  group,
  toggleCollapsed,
  isCollapsed,
  hasDivider = true,
}: Props<T>) => {
  return (
    <Stack key={group.name} gap={16}>
      <Group justify="space-between" style={{ cursor: 'pointer' }} onClick={() => toggleCollapsed(group)}>
        <Group gap="xs">
          {group.icon && <Icon name={group.icon} size={16} color={inkLight} style={{ cursor: 'pointer' }} />}
          <Text weight="medium" size="small">
            {group.name}
          </Text>
        </Group>
        <Icon
          name={isCollapsed ? 'unfold_more' : 'unfold_less'}
          size={16}
          color={inkLight}
          style={{ cursor: 'pointer' }}
        />
      </Group>

      {!isCollapsed && (
        <RadioGroup role="radiogroup" aria-labelledby={`${group.name}-label`} id={`${group.name}-panel`}>
          {group.options.map((option) => (
            <Radio
              key={option.value}
              label={option.label}
              value={option.value}
              checked={group.selectedValue === option.value}
              onChange={() => group.onSelectionChange(option.value)}
              aria-checked={group.selectedValue === option.value}
            />
          ))}
        </RadioGroup>
      )}
      {hasDivider && <Divider />}
    </Stack>
  );
};

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
